import React from 'react';
import { createFormBag, FormBag, FormData, FormErrors, FormUpdateEvent, validateFormBag } from 'react-formage';

import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/forms'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu';
import { AttributeBlock } from 'components/ui/attribute-block';
import { PrimaryNavigationEditPosition } from 'components/ui/primary-navigation/edit-position'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { Mapbox, MapInitialBounds, MapView } from 'components/mapbox';
import { EditableBoundaryMapSet } from 'components/mapsets/editable-polygon';
import { NavigationControlSet } from 'components/mapsets/navigation-control';
import { RefCopy } from 'components/ui/ref-copy';
import { S3Uploader, SignResult } from 'components/s3-uploader';
import { AddAttributeButton } from 'components/ui/add-attribute-button';
import { Alert, ConfirmDelete } from 'components/ui/modal';
import { sharedActionCreators } from 'containers/shared';
import { connect } from 'containers/store';
import { AdminMap, BuildingLevel } from 'generated/mos/structure';
import { assertNever, pick, shallowEqual } from 'helpers/core';
import * as geo from 'helpers/geo';
import { Status, statusSelector } from 'helpers/status';
import { space } from 'helpers/style'

import { LayoutLocateEditPage } from 'layouts/locate-edit-page';

import { structuresActionCreators } from 'domains/structures/actions';
import { buildAdminMapCorners } from 'domains/structures/entities';
import { AdminMapSet, BoundarySet } from 'domains/structures/mapsets';
import { AdminMapEditSet, Dimensions } from 'domains/structures/mapsets/admin-map-edit-set';
import { StructuresAppState } from 'domains/structures/store';

import styled from 'styled';

const SectionHeader = styled.div`
  color: ${({ theme }) => theme.color.darkText};
  margin-top: ${space(9)};
  margin-bottom: ${space(9)};
  font-size: 20px;
`;

const Section = styled.div`
  padding: 0 ${space(6)};
`;

const LastButtonRow = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-grow: 1;
  justify-content: space-between;
  align-items: flex-end;
  padding: ${space(6)};
`;

const NoBoundaryLabel = styled.div`
  border-top: 1px solid ${({ theme }) => theme.color.grayL80};
  border-bottom: 1px solid ${({ theme }) => theme.color.grayL80};
  padding: ${space(3)} ${space(4)};
`;

type DirectProps = {
  readonly buildingLevelId: string;
  readonly onComplete: () => void;
};

type ConnectedProps = Pick<StructuresAppState, 'buildingLevelEdit' | 'structures' | 'globalMap'>;

type ActionProps =
  Pick<typeof sharedActionCreators, 'toastNotification'> &
  Pick<typeof structuresActionCreators,
    'structuresLoadRequest' | 'structuresFiltersUpdate' |
    'buildingLevelEditSaveRequest' | 'buildingLevelEditLoadRequest' | 'buildingLevelEditReset' | 'buildingLevelEditDeleteRequest'>
  ;

type Props = ActionProps & ConnectedProps & DirectProps;

type EditMode = undefined | 'adminmap' | 'boundary' | 'adminmapImage';

type State = {
  readonly buildingLevelEditReady?: boolean;
  readonly showBoundaryModal?: boolean;
  readonly showConfirmDeleteModal?: boolean;
  readonly isEditingBoundary?: boolean;
  readonly isEditingAdminMap?: boolean;

  readonly editMode: EditMode;
  readonly showDismissableErrorModal: boolean;
  readonly errorModalMessage: string;

  readonly boundary?: geo.Polygon2D;
  readonly boundaryPrevious?: geo.Polygon2D;

  readonly adminMapUrl?: string;
  readonly adminMapUrlPrevious?: string;
  readonly adminMapCornersPrevious?: geo.Corners;
  readonly adminMapCorners?: geo.Corners;
  readonly adminMapDimensions?: Dimensions;
  readonly adminMapUploading?: boolean;
  readonly shortName?: string; // XXX: these two are not currently in use
  readonly zOrder?: number; // XXX: these two are not currently in use

  // The globalMap's view is only used for a default if the beacon is
  // unpositioned.
  readonly mapView?: MapView;

  // Replace this with a new '{}' to force mapbox to update to the initial bounds:
  readonly mapBoundsIdentity?: object;
  readonly mapBounds?: geo.BBox2D;

  readonly bag: FormBag<FormValues>;
  readonly initialFormValues: FormValues;
};

type FormValues = {
  readonly name: string;
  readonly zOrder: number;
};

// TODO(dc): copy-pasted from site-edit-page
const deriveAdminMapState = (adminMap?: AdminMap.Entity) => {
  if (!adminMap) return {};
  const dimensions = {
    width: adminMap.imageWidth,
    height: adminMap.imageHeight,
  };
  return {
    adminMapUrl: adminMap.imageUrl,
    adminMapCorners: buildAdminMapCorners(adminMap),
    adminMapDimensions: dimensions.width && dimensions.height ? dimensions : undefined,
  };
}

// XXX: this (or something much better than it) may belong as a core helper
const getImageDimensions = async (url: string): Promise<Dimensions> => {
  const img = new Image();
  return new Promise((resolve) => {
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.src = url;
  });
};


class BuildingLevelEditPageView extends React.Component<Props, State> {
  public constructor(props: Props) {
    super(props);
    const initialFormValues = { name: '', zOrder: 0 }
    this.state = {
      editMode: undefined,
      adminMapUrl: undefined,
      adminMapUrlPrevious: undefined,
      bag: createFormBag(initialFormValues),
      initialFormValues,
      showDismissableErrorModal: false,
      errorModalMessage: '',
    };
  }

  public static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (state.buildingLevelEditReady) return null;

    if (!statusSelector.hasData(props.buildingLevelEdit) ||
      !statusSelector.hasData(props.structures)) {
      return null;
    }

    const buildingLevel = props.buildingLevelEdit.data.entity;
    const adminMap = props.buildingLevelEdit.data.adminMapEntity;
    if (!buildingLevel) {
      throw new Error();
    }

    let mapView: MapView = props.globalMap.view;
    if (buildingLevel.boundary) {
      mapView = {
        ...mapView,
        center: geo.centroid2D({
          type: 'Polygon',
          coordinates: buildingLevel.boundary.coordinates as any, // FIXME: geo-jank
        }),
      };
    }

    const initialFormValues: FormValues = {
      name: buildingLevel.name,
      zOrder: buildingLevel.zOrder,
    }

    const newState: Partial<State> = {
      buildingLevelEditReady: true,
      ...deriveAdminMapState(adminMap || undefined),
      boundary: geo.optionalPolygon2D(buildingLevel.boundary),
      shortName: buildingLevel.shortName,
      zOrder: buildingLevel.zOrder,
      bag: createFormBag(initialFormValues),
      initialFormValues,
      mapView,
    };

    return newState;
  }

  private get ref(): BuildingLevel.Ref {
    return { typename: 'mos.structure.BuildingLevel', id: this.props.buildingLevelId };
  }

  private get name() { return this.state.bag.values.name; }

  private mapBounds(): MapInitialBounds | undefined {
    if (!this.state.mapBoundsIdentity || !this.state.mapBounds) {
      return;
    }
    return {
      padding: 120, // FIXME put in context
      identity: this.state.mapBoundsIdentity,
      bounds: this.state.mapBounds,
    };
  }

  public componentDidMount() {
    this.props.buildingLevelEditLoadRequest(this.ref);
    this.refresh();
  }

  public componentDidUpdate() {
    if (this.props.buildingLevelEdit.status === Status.Complete) {
      this.props.toastNotification({ type: 'success', text: this.props.buildingLevelEdit.message });
      this.props.onComplete();
    }
    this.refresh();
  }

  private refresh() {
    if (statusSelector.shouldLoad(this.props.structures.status)) {
      this.props.structuresLoadRequest();
    }
  }

  public componentWillUnmount() {
    this.props.buildingLevelEditReset();
  }

  private onConfirmDelete() {
    this.props.buildingLevelEditDeleteRequest({
      onSuccess: () => {
        this.props.toastNotification({ type: 'success', text: `Building level deleted` });
        this.props.onComplete();
      },
    });
  }

  private onFormUpdate = (e: FormUpdateEvent<FormValues>) => {
    this.setState({ bag: e.bag });
  }

  // {{{ Admin map handlers

  private onAdminMapShow = () => {
    if (this.state.adminMapCorners) {
      this.setState({ mapBoundsIdentity: {}, mapBounds: geo.asBBox2D(this.state.adminMapCorners) });
    }
  }

  private onAdminMapPosEdit = () => {
    if (!this.state.adminMapUrl) {
      throw new Error();
    }
    this.setState({
      editMode: 'adminmap',
      adminMapCornersPrevious: this.state.adminMapCorners,
    });
  }

  private resolveAdminMap(complete: SignResult): void {
    this.setState({ adminMapUploading: true });

    // Strip all the AWS signing query parameters from the URL that we send to the backend:
    const url = new URL(complete.signedUrl);
    url.search = '';
    const imageUrl = url.toString();

    getImageDimensions(imageUrl)
      .then((dimensions) => {
        this.setState({
          editMode: 'adminmap',
          adminMapUploading: false,
          adminMapUrl: imageUrl,
          adminMapDimensions: dimensions,
        });
      });
  }

  private resolveReplacementAdminMap(complete: SignResult): void {
    this.setState({ adminMapUploading: true });

    // Strip all the AWS signing query parameters from the URL that we send to the backend:
    const url = new URL(complete.signedUrl);
    url.search = '';
    const imageUrl = url.toString();

    const adminMapUrlPrevious = this.state.adminMapUrl

    this.setState({
      ...this.state,
      adminMapUrlPrevious,
      editMode: 'adminmapImage',
      adminMapUploading: false,
      adminMapUrl: imageUrl,
    });
  }

  private onAdminMapPosCommit = () => {
    this.setState({ editMode: undefined });
  }

  private onAdminMapCancel = () => {
    this.setState({
      editMode: undefined,
      adminMapCorners: geo.optionalCorners(this.state.adminMapCornersPrevious),
    });
  }

  private onAdminMapUpdateCancel = () => {
    this.setState({
      ...this.state,
      editMode: undefined,
      adminMapUrl: this.state.adminMapUrlPrevious,
      adminMapUrlPrevious: undefined,
    });
  }

  private onAdminMapPosUpdate = (e: { corners: geo.Corners }) => {
    if (!this.state.adminMapUrl) {
      throw new Error();
    }
    this.setState({ adminMapCorners: e.corners });
  }

  private onAdminMapRemove = () => {
    this.setState({ adminMapUrl: undefined, adminMapCorners: undefined, editMode: undefined });
  }

  // }}}


  // {{{ Boundary handlers

  private onBoundaryShow = () => {
    if (this.state.boundary) {
      this.setState({ mapBoundsIdentity: {}, mapBounds: geo.bbox2D(this.state.boundary) });
    }
  }

  private onBoundaryAdd = () => {
    if (this.state.boundary) {
      this.setState({ showDismissableErrorModal: true, errorModalMessage: `'${this.name}' already has a boundary.` });
      return;
    }
    this.setState({ editMode: 'boundary' });
  }

  private onBoundaryEdit = () => this.setState({ editMode: 'boundary', boundaryPrevious: this.state.boundary });
  private onBoundaryCancel = () => this.setState({ editMode: undefined, boundary: this.state.boundaryPrevious });
  private onBoundaryPosCommit = () => this.setState({ editMode: undefined });
  private onBoundaryRemove = () => this.setState({ boundary: undefined });

  private onBoundaryUpdate = (e: { polygon: geo.Polygon2D | undefined }) => {
    this.setState({ boundary: e.polygon });
  }

  // }}}


  private onFormValidate = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {};
    if (!values.name.trim()) {
      errors.name = 'Required';
    }
    // Check if there is a value and honor 0 as a valid value
    if (!values.zOrder && values.zOrder < 0) {
      errors.zOrder = 'Required';
    }
    return errors;
  }

  private save = () => {
    if (this.state.editMode) {
      throw new Error();
    }
    if (this.props.buildingLevelEdit.status !== Status.Ready) return;

    const bag = validateFormBag(this.state.bag, this.onFormValidate);
    this.setState({ bag });
    if (!bag.valid) {
      return;
    }

    this.props.structuresFiltersUpdate({ lastEdited: this.ref });
    let adminMapCreate, adminMapUpdate;

    const data = statusSelector.data(this.props.buildingLevelEdit)

    if (data && data.adminMapEntity && this.state.adminMapUrl === data.adminMapEntity.imageUrl && data.adminMapEntity.ref) {
      adminMapUpdate = {
        ref: data.adminMapEntity.ref,
        bottomLeftGeoReference: {
          type: 'Point' as const,
          coordinates: this.state.adminMapCorners![3],
        },
        topRightGeoReference: {
          type: 'Point' as const,
          coordinates: this.state.adminMapCorners![1],
        },
      };
    } else if (this.state.adminMapUrl) {
      adminMapCreate = {
        imageUrl: this.state.adminMapUrl,
        bottomLeftGeoReference: {
          type: 'Point' as const,
          coordinates: this.state.adminMapCorners![3],
        },
        topRightGeoReference: {
          type: 'Point' as const,
          coordinates: this.state.adminMapCorners![1],
        },
      };
    }

    this.props.buildingLevelEditSaveRequest(this.ref, {
      buildingRef: this.props.buildingLevelEdit.data.entity.buildingRef!,
      name: this.name,
      boundary: this.state.boundary,
      shortName: this.state.shortName || '',
      zOrder: this.state.bag.values.zOrder,
      adminMapUpdate,
      adminMapCreate,
      adminMapDeleteRef: !this.state.adminMapUrl && data && data.adminMapEntity ? data.adminMapEntity.ref : undefined
    });
  }

  private onCancelEdit = () => {
    this.props.onComplete();
  }

  private isModified = (): boolean => {
    if (!statusSelector.hasData(this.props.buildingLevelEdit) ||
      !statusSelector.hasData(this.props.structures)) {
      return false;
    }

    // check if form has changed
    if (!shallowEqual(this.state.bag.values, this.state.initialFormValues)) {
      return true;
    }

    // check if admin map or boundary has changed
    const buildingLevelEdit = this.props.buildingLevelEdit.data;
    const initialBoundary = buildingLevelEdit.entity.boundary;
    const initialAdminMapUrl = buildingLevelEdit.adminMapEntity ? buildingLevelEdit.adminMapEntity.imageUrl : undefined;
    const initialAdminMapCorners = buildingLevelEdit.adminMapEntity ? buildAdminMapCorners(buildingLevelEdit.adminMapEntity) : undefined;

    if (!geo.equal(initialBoundary, this.state.boundary) ||
      !geo.equal(initialAdminMapCorners, this.state.adminMapCorners) ||
      initialAdminMapUrl !== this.state.adminMapUrl) {
      return true;
    }

    return false;
  }

  public render() {
    if (this.props.buildingLevelEdit.status === Status.Failed) {
      // FIXME: el-cheapo error display
      return this.props.buildingLevelEdit.messages.map((v) => v.text).join(', ');
    }

    const saveDisabled = this.state.isEditingAdminMap ||
      this.state.isEditingBoundary ||
      this.props.buildingLevelEdit.status === Status.Updating;

    const { errors, touched } = this.state.bag;

    const modified = this.isModified()

    const sidebar = () => (
      <>
        {!this.state.showConfirmDeleteModal
          ? null
          : <ConfirmDelete
            title={`Delete '${this.name}'?`}
            message="Deleting a building level will in turn delete all associated spaces, beacons, and properties"
            checklist={['All level properties will be deleted', 'All beacons and spaces will be unpositioned', 'This cannot be undone.']}
            confirmLabel="Yes, delete this level"
            onConfirm={() => this.onConfirmDelete()}
            onClose={() => this.setState({ showConfirmDeleteModal: false })}
          />
        }

        {!this.state.showBoundaryModal
          ? null
          : <Alert
            onClose={() => this.setState({ showBoundaryModal: false })}
            message="You can only add one boundary. Please remove or edit the existing boundary."
          />
        }

        {!this.state.showDismissableErrorModal
          ? null
          : <Alert
            onClose={() => this.setState({ showDismissableErrorModal: false })}
            message={this.state.errorModalMessage}
          />
        }

        <div>
          <Section>
            <SectionHeader>Details</SectionHeader>

            <FormData bag={this.state.bag} onUpdate={this.onFormUpdate} validate={this.onFormValidate}>
              <Input
                label="Building name"
                field="name"
                error={touched.name ? errors.name : undefined}
              />
              <Input
                label="List order"
                field="zOrder"
                error={touched.name ? errors.name : undefined}
              />
            </FormData>
          </Section>

          <Section style={{ paddingBottom: 60 }}>
            <Label>Admin map overlay</Label>
            {!this.state.adminMapUrl
              ? <S3Uploader
                htmlId="admin-map-upload"
                label="Upload image"
                disabled={this.state.adminMapUploading || !!this.state.editMode}
                onComplete={(complete) => this.resolveAdminMap(complete)}
              />
              : <>
                <AttributeBlock title={this.state.adminMapUrl.substring(this.state.adminMapUrl.lastIndexOf('/') + 1)}>
                  <PopOverMenu>
                    <PopOverMenuButton onClick={this.onAdminMapPosEdit}>Edit alignment</PopOverMenuButton>
                    <PopOverMenuButton onClick={this.onAdminMapShow}>Centre on map</PopOverMenuButton>
                  </PopOverMenu>
                </AttributeBlock>
                <S3Uploader
                  htmlId="admin-map-upload"
                  label="Replace admin map overlay"
                  disabled={this.state.adminMapUploading || !!this.state.editMode}
                  onComplete={(complete) => this.resolveReplacementAdminMap(complete)}
                />
              </>
            }
          </Section>

          <Section>
            <SectionHeader>Attributes</SectionHeader>
            <AddAttributeButton title="Boundary" onClick={this.onBoundaryAdd} />
            {!this.state.boundary
              ? <NoBoundaryLabel>No boundary set</NoBoundaryLabel>
              : <AttributeBlock title="Level Boundary">
                <PopOverMenu>
                  <PopOverMenuButton onClick={this.onBoundaryEdit}>Edit boundary</PopOverMenuButton>
                  <PopOverMenuButton onClick={this.onBoundaryShow}>Centre on map</PopOverMenuButton>
                </PopOverMenu>
              </AttributeBlock>
            }
          </Section>
        </div>

        <LastButtonRow>
          <Button
            onClick={() => this.setState({ showConfirmDeleteModal: true })}
            isDisabled={saveDisabled}
          >
            Delete level
          </Button>
          <RefCopy modelRef={this.ref} />
        </LastButtonRow>
      </>
    );

    let nav: () => React.ReactNode;
    if (this.state.editMode === 'adminmap') {
      nav = () => (
        <PrimaryNavigationEditPosition
          title="Editing map position"
          kind="overlay"
          onCommit={this.onAdminMapPosCommit}
          onCancel={this.onAdminMapCancel}
          onDelete={this.onAdminMapRemove}
        />
      );
    } else if (this.state.editMode === 'adminmapImage') {
      nav = () => (
        <PrimaryNavigationEditPosition
          title="Replace admin map and position"
          kind="overlay"
          onCommit={this.onAdminMapPosCommit}
          onCancel={this.onAdminMapUpdateCancel}
          onDelete={this.onAdminMapRemove}
        />
      );
    } else if (this.state.editMode === 'boundary') {
      nav = () => (
        <PrimaryNavigationEditPosition
          title="Edit building level boundary"
          kind="boundary"
          onCommit={this.onBoundaryPosCommit}
          onCancel={this.onBoundaryCancel}
          onDelete={this.onBoundaryRemove}
        />
      );

    } else if (!this.state.editMode) {
      nav = () => (
        <PrimaryNavigationEditEntity
          title="Edit building level"
          onSave={this.save}
          disabled={!modified}
          saveDisabled={!modified}
          onCancel={this.onCancelEdit}
          onConfirm={modified ? this.onCancelEdit : undefined}
          navBackRoute={!modified ? '/locate/structures' : undefined}
        />
      );

    } else {
      assertNever(this.state.editMode); // Exhaustiveness check
      throw new Error(); // TypeScript requires this
    }

    let boundaryEditSet: EditableBoundaryMapSet | undefined;
    let boundarySet: BoundarySet | undefined;

    if (this.state.editMode === 'boundary') {
      boundaryEditSet = new EditableBoundaryMapSet({
        key: 'activeStructure',
        boundary: this.state.boundary,
        onUpdated: this.onBoundaryUpdate,
      });

    } else if (this.state.boundary) {
      boundarySet = new BoundarySet({ key: 'structhover', boundaries: [this.state.boundary] });
    }

    const adminMapViewSet = this.state.editMode !== 'adminmap' && this.state.adminMapUrl && this.state.adminMapCorners
      ? new AdminMapSet('adminmap', this.state.adminMapUrl, this.state.adminMapCorners)
      : undefined;

    const adminMapEditSet = (this.state.editMode === 'adminmap' || this.state.editMode === 'adminmapImage') && this.state.adminMapUrl && this.state.adminMapDimensions
      ? new AdminMapEditSet({
        key: 'adminmapedit',
        imageUrl: this.state.adminMapUrl,
        corners: this.state.adminMapCorners,
        dimensions: this.state.adminMapDimensions,
        onUpdated: this.onAdminMapPosUpdate,
      })
      : undefined;

    const mapSets = [adminMapViewSet, boundarySet, boundaryEditSet, adminMapEditSet, new NavigationControlSet({ showCompass: false })];

    return (
      <LayoutLocateEditPage
        nav={nav}
        sidebar={sidebar}
        content={() => (
          <React.Fragment>
            <Mapbox
              view={this.state.mapView!}
              initialBounds={this.mapBounds()}
              onViewChanged={(e) => this.setState({ mapView: e })}
              mapSets={mapSets}
            />
          </React.Fragment>
        )}
      />
    );
  }
}

export const BuildingLevelEditPage = connect<ConnectedProps, ActionProps, DirectProps>(
  (store) => pick(store.structures, 'buildingLevelEdit', 'globalMap', 'structures'),
  {
    ...pick(structuresActionCreators, 'structuresLoadRequest', 'buildingLevelEditLoadRequest', 'buildingLevelEditReset', 'buildingLevelEditDeleteRequest', 'buildingLevelEditSaveRequest', 'structuresFiltersUpdate'),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(BuildingLevelEditPageView);
